import { LenderAssetReward, RewardSourceRef, RewardStream } from '../types'
import {
  MerklOpportunity,
  ReserveTokenRewardResult,
  RewardFetchOutput,
  RewardFetcher,
  RewardResult,
} from './types'

const MERKL_API_BASE = 'https://api.merkl.xyz/v4'

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Fetch live Merkl opportunities for a protocol (handles pagination).
 *
 * `chainId` is optional — omitting it returns campaigns on every chain in a
 * single paginated sweep. Prefer that for protocols whose deployment list moves
 * (Euler adds chains constantly): a hardcoded chain list silently drops
 * campaigns, whereas an unfiltered fetch is filtered downstream anyway by the
 * asset/vault registry join.
 */
async function fetchMerklOpportunities(
  protocolId: string,
  chainId?: string,
): Promise<MerklOpportunity[]> {
  const all: MerklOpportunity[] = []
  let page = 0

  while (true) {
    const params = new URLSearchParams({
      mainProtocolId: protocolId,
      status: 'LIVE',
      items: '100',
      page: String(page),
    })
    if (chainId) params.set('chainId', chainId)
    const res = await fetch(`${MERKL_API_BASE}/opportunities?${params}`)
    if (!res.ok)
      throw new Error(`Merkl API ${res.status} for chain ${chainId ?? 'all'}`)
    const batch: MerklOpportunity[] = await res.json()
    all.push(...batch)
    if (batch.length < 100) break
    page++
  }

  return all
}

function emptyReward(): LenderAssetReward {
  return {
    distribution: 'merkle',
    deposit: 0,
    borrow: 0,
    additionalDepositData: [],
    additionalBorrowData: [],
  }
}

function ensureReserveTokenReward(
  result: ReserveTokenRewardResult,
  chainId: string,
  reserveToken: string,
): LenderAssetReward {
  if (!result[chainId]) result[chainId] = {}
  const key = reserveToken.toLowerCase()
  if (!result[chainId][key]) result[chainId][key] = emptyReward()
  return result[chainId][key]
}

function ensureReward(
  result: RewardResult,
  chainId: string,
  lender: string,
  asset: string,
): LenderAssetReward {
  if (!result[chainId]) result[chainId] = {}
  if (!result[chainId][lender]) result[chainId][lender] = {}
  if (!result[chainId][lender][asset])
    result[chainId][lender][asset] = emptyReward()
  return result[chainId][lender][asset]
}

function getMerkleUrl(opp: MerklOpportunity): string {
  // example: https://app.merkl.xyz/opportunities/mantle/AAVE_NET_LENDING/ba71336ef6f94175
  return `https://app.merkl.xyz/opportunities/${opp.chain.name}/${opp.type}/${opp.identifier}`
}

/** Extract per-reward-token breakdowns from a Merkl opportunity */
function extractRewardBreakdowns(opp: MerklOpportunity) {
  if (!opp.rewardsRecord?.breakdowns?.length) return []
  return opp.rewardsRecord.breakdowns.map((b) => ({
    token: b.token.symbol,
    tokenAddress: b.token.address,
    dailyUsdValue: b.value,
    distributionType: b.distributionType,
  }))
}

/** Coerce Merkl's stringly-typed unix seconds; drop anything unusable. */
function unixSeconds(v: number | string | undefined): number | undefined {
  if (v === undefined || v === null) return undefined
  const n = typeof v === 'number' ? v : Number(v)
  return Number.isFinite(n) && n > 0 ? n : undefined
}

/**
 * Split an opportunity's APR across the tokens it actually pays, as typed
 * streams.
 *
 * Merkl reports one APR for the whole opportunity but pays potentially several
 * tokens, and the previous model kept only `breakdowns[0]` — so a two-token
 * campaign was recorded as one token earning the combined APR. Two joins fix
 * that, in order of precision:
 *
 *  1. **Exact.** `aprRecord.breakdowns[].identifier` is the on-chain campaign
 *     id and equals `rewardsRecord.breakdowns[].onChainCampaignId`, so each
 *     campaign's own APR lands on the token that campaign pays.
 *  2. **Pro-rata.** Where that join misses (no `aprRecord`, or ids absent), the
 *     opportunity APR is split by each token's share of daily USD payout —
 *     exact in aggregate, since APR is proportional to reward value over the
 *     same TVL.
 *
 * With neither, a single stream carries the whole APR and no token, which is
 * still strictly better than attributing it to an arbitrary one.
 */
function buildMerklStreams(
  opp: MerklOpportunity,
  side: 'deposit' | 'borrow',
  totalApr: number,
  source: RewardSourceRef,
): RewardStream[] {
  const rewardBreakdowns = opp.rewardsRecord?.breakdowns ?? []
  // Merkl serialises these as unix-second STRINGS ("1786453200"), so they must
  // be coerced — a consumer doing arithmetic on the raw value gets string
  // concatenation, and the JSON we publish would disagree with its own schema.
  const endsAt = unixSeconds(opp.earliestCampaignEnd ?? opp.latestCampaignEnd)
  const startsAt = unixSeconds(
    opp.earliestCampaignStart ?? opp.latestCampaignStart,
  )

  const base = {
    side,
    kind: 'token' as const,
    claim: 'merkl' as const,
    endsAt,
    startsAt,
  }

  if (!rewardBreakdowns.length) {
    return [{ ...base, apr: totalApr, token: undefined, source }]
  }

  const aprByCampaign = new Map<string, number>()
  for (const b of opp.aprRecord?.breakdowns ?? []) {
    if (b?.identifier && Number.isFinite(b.value))
      aprByCampaign.set(b.identifier.toLowerCase(), b.value)
  }

  const totalDaily = rewardBreakdowns.reduce((s, b) => s + (b.value || 0), 0)

  return rewardBreakdowns.map((b) => {
    const campaignApr = b.onChainCampaignId
      ? aprByCampaign.get(b.onChainCampaignId.toLowerCase())
      : undefined
    // Pro-rata only when the exact join missed. `totalDaily <= 0` means Merkl
    // reported no payout values at all, in which case an even split is the
    // only defensible fallback.
    const apr =
      campaignApr ??
      (totalDaily > 0
        ? (totalApr * (b.value || 0)) / totalDaily
        : totalApr / rewardBreakdowns.length)

    return {
      ...base,
      apr,
      token: {
        address: b.token.address.toLowerCase(),
        chainId: String(opp.chainId),
        symbol: b.token.symbol,
        decimals: b.token.decimals,
        logoURI: b.token.icon,
        priceUsd: b.token.price ?? undefined,
      },
      dailyRewardsUsd: b.value,
      source: {
        ...source,
        refs: {
          ...(source.refs ?? {}),
          ...(b.onChainCampaignId
            ? { onChainCampaignId: b.onChainCampaignId }
            : {}),
          ...(b.campaignId ? { campaignId: b.campaignId } : {}),
          ...(b.distributionType
            ? { distributionType: b.distributionType }
            : {}),
        },
      },
    }
  })
}

/**
 * Identity of the program, as opposed to the mechanism.
 *
 * `LenderAssetReward.distribution` is `'merkle'` for every campaign we read,
 * which cannot tell an Aave program from a Euler one. The slug here is what a
 * consumer keys on, the label is what it renders, and `link` is the exact
 * opportunity page — not a protocol homepage.
 */
function merklSourceRef(
  opp: MerklOpportunity,
  merklProtocolId: string,
): RewardSourceRef {
  const protocolName = opp.protocol?.name ?? merklProtocolId
  return {
    id: `merkl:${merklProtocolId}`,
    label: `Merkl · ${protocolName}`,
    link: getMerkleUrl(opp),
    platform: 'merkl',
    refs: {
      opportunityId: opp.identifier,
      opportunityType: opp.type,
      chain: opp.chain?.name ?? String(opp.chainId),
      ...(opp.name ? { opportunityName: opp.name } : {}),
      ...(opp.liveCampaigns ? { liveCampaigns: opp.liveCampaigns } : {}),
    },
  }
}

/**
 * Resolve the underlying asset address from a Merkl opportunity.
 *
 * Strategy (in order):
 *  1. Parse `underlyingAsset` from `depositUrl` query string
 *  2. Find the first token that is neither the aToken (explorerAddress)
 *     nor a debt token
 *  3. Fall back to tokens[0]
 */
function resolveUnderlyingFromOpportunity(
  opp: MerklOpportunity,
): string | undefined {
  if (!opp.tokens?.length) return undefined

  // 1. Try depositUrl — Aave URLs contain ?underlyingAsset=0x...
  if (opp.depositUrl) {
    try {
      const url = new URL(opp.depositUrl)
      const underlying = url.searchParams.get('underlyingAsset')
      if (underlying) return underlying.toLowerCase()
    } catch {
      // not a valid URL, move on
    }
  }

  // 2. Filter out aToken (matches explorerAddress) and debt tokens
  const explorer = opp.explorerAddress?.toLowerCase()
  const underlying = opp.tokens.find(
    (t) =>
      t.address.toLowerCase() !== explorer &&
      !t.symbol.startsWith('variableDebt') &&
      !t.symbol.startsWith('stableDebt'),
  )
  if (underlying) return underlying.address.toLowerCase()

  // 3. Fallback
  return opp.tokens[0].address.toLowerCase()
}

// ---------------------------------------------------------------------------
// Factory
// ---------------------------------------------------------------------------

export interface MerklRewardConfig {
  /** Protocol ID in Merkl's system (e.g. "aave", "morpho", "compound") */
  merklProtocolId: string
  /** Default lender key in our system (e.g. "AAVE_V3") */
  lenderKey: string
  /**
   * Chain IDs to fetch rewards for. Omit to sweep every chain in one request —
   * do that whenever the protocol's chain list is not stable enough to hardcode.
   */
  chainIds?: string[]
  /**
   * Optional resolver: given an opportunity return the underlying asset
   * address and optionally override the lender key. May return several
   * resolutions for campaigns that span multiple markets.
   * Defaults to resolveUnderlyingFromOpportunity().
   */
  resolveAsset?: (
    opp: MerklOpportunity,
  ) => AssetResolution | AssetResolution[] | undefined
  /**
   * Emit rewards keyed by RESERVE TOKEN instead of (lender, underlying).
   *
   * For protocols whose Merkl campaigns span several deployments under one
   * `mainProtocolId` and don't say which — Aave: V3 mainnet, Horizon, Prime,
   * Ether.fi and every V4 hub/spoke all arrive as `aave` — the lender key is a
   * guess, and a wrong guess both mis-files the campaign and (because several
   * deployments then share a bucket) sums unrelated campaigns together. The
   * reserve token the campaign pays on identifies the market exactly, so
   * `resolveAsset` returns that instead and the consumer joins it against each
   * market's own `params.metadata.{aToken, vToken}`.
   */
  keyByReserveToken?: boolean
}

type RewardBucket = 'deposit' | 'borrow'

type AssetResolution =
  | {
      asset: string
      lender?: string
      bucket?: RewardBucket
      /**
       * Which map this resolution belongs in, overriding the fetcher default.
       * Aave needs both: V4 campaigns name their spoke/hub contract, which IS
       * our lender key, so those are lender-keyed; V3-family campaigns only
       * identify themselves by reserve token.
       */
      keyBy?: 'lender' | 'reserveToken'
    }
  | undefined

/** Create a RewardFetcher that pulls data from the Merkl API for a given protocol */
export function createMerklRewardFetcher(
  config: MerklRewardConfig,
): RewardFetcher {
  const defaultResolve = (opp: MerklOpportunity): AssetResolution => {
    const asset = resolveUnderlyingFromOpportunity(opp)
    if (!asset) return undefined
    return { asset }
  }

  const resolve = config.resolveAsset ?? defaultResolve

  return {
    label: `MERKL_${config.merklProtocolId.toUpperCase()}`,
    fetch: async (): Promise<RewardFetchOutput> => {
      const result: RewardResult = {}
      const byReserveToken: ReserveTokenRewardResult = {}

      // With no chainIds configured, one unfiltered sweep covers every chain.
      // Otherwise fetch the configured chains in parallel — individual chain
      // failures don't break the entire fetch, we just skip that chain.
      const chainResults = config.chainIds
        ? await Promise.all(
            config.chainIds.map(async (chainId) => {
              try {
                return await fetchMerklOpportunities(
                  config.merklProtocolId,
                  chainId,
                )
              } catch (e) {
                console.error(`Merkl: failed to fetch chain ${chainId}:`, e)
                return [] as MerklOpportunity[]
              }
            }),
          )
        : [await fetchMerklOpportunities(config.merklProtocolId)]

      for (const opps of chainResults) {
        for (const opp of opps) {
          if (!opp.apr || opp.apr <= 0) continue

          // Key off the opportunity's own chain, not the requested one — an
          // unfiltered sweep returns every chain in a single batch.
          const chainId = String(opp.chainId)

          const resolvedRaw = resolve(opp)
          if (!resolvedRaw) continue
          const resolutions = (
            Array.isArray(resolvedRaw) ? resolvedRaw : [resolvedRaw]
          ).filter((r): r is NonNullable<AssetResolution> => Boolean(r?.asset))

          for (const resolved of resolutions) {
            // Bucket resolution: an explicit `bucket` from resolveAsset wins — e.g.
            // Morpho collateral incentives are always a deposit-as-collateral reward
            // even when Merkl tags the opp action as BORROW. Otherwise derive from
            // the action, skipping anything that isn't a plain supply/borrow opp.
            const bucket: RewardBucket | undefined =
              resolved.bucket ??
              (opp.action === 'LEND'
                ? 'deposit'
                : opp.action === 'BORROW'
                  ? 'borrow'
                  : undefined)
            if (!bucket) continue

            // `keyByReserveToken` protocols skip the lender dimension entirely:
            // the reserve token already pins the market, and guessing a lender
            // key is what mis-filed every Aave deployment onto AAVE_V3. A
            // resolution may opt back into lender keying when it *can* name the
            // deployment (Aave V4 campaigns carry their spoke/hub address).
            const keyBy =
              resolved.keyBy ??
              (config.keyByReserveToken ? 'reserveToken' : 'lender')
            const reward =
              keyBy === 'reserveToken'
                ? ensureReserveTokenReward(
                    byReserveToken,
                    chainId,
                    resolved.asset,
                  )
                : ensureReward(
                    result,
                    chainId,
                    resolved.lender ?? config.lenderKey,
                    resolved.asset,
                  )
            const breakdowns = extractRewardBreakdowns(opp)
            reward.link = getMerkleUrl(opp)

            // Typed, fully-referenced view of the same campaign. Appended, not
            // substituted: `additional*Data` and `link` stay exactly as they
            // were so the yield-tracer recorder is unaffected until it migrates.
            const source = merklSourceRef(opp, config.merklProtocolId)
            if (!reward.streams) reward.streams = []
            reward.streams.push(
              ...buildMerklStreams(opp, bucket, opp.apr, source),
            )

            if (bucket === 'deposit') {
              reward.deposit += opp.apr
              reward.additionalDepositData.push(...breakdowns)
            } else {
              reward.borrow += opp.apr
              reward.additionalBorrowData.push(...breakdowns)
            }
          }
        }
      }

      // Both maps can be populated at once: Aave files its V4 campaigns by
      // lender (the spoke address IS the lender key) and everything else by
      // reserve token, so returning only one of them would silently drop half.
      return { byLender: result, byReserveToken }
    },
  }
}

// ---------------------------------------------------------------------------
// Pre-configured fetcher instances
// ---------------------------------------------------------------------------

/**
 * No `chainIds` — Aave V3's deployment list outgrew the hardcoded one (Monad and
 * Plasma campaigns were being dropped despite both being tracked in
 * `aave-tokens.json`). Sweep every chain and let the reserve join filter.
 */
export const aaveMerklRewardFetcher = createMerklRewardFetcher({
  merklProtocolId: 'aave',
  lenderKey: 'AAVE_V3',
  // Aave campaigns are keyed by the reserve token they pay on, NOT by lender:
  // Merkl's `aave` protocol id covers V3 mainnet, Horizon, Prime, Ether.fi and
  // every V4 hub/spoke, and nothing in the payload identifies the deployment
  // reliably — "Borrow USDC on Aave" advertises `marketName=proto_mainnet` in
  // its depositUrl while paying on `variableDebtHorRwaUSDC`, i.e. Horizon.
  // Filing them all under AAVE_V3 summed unrelated deployments into one number
  // (V3 USDC borrow read 5.75% = 1.75 + 2 + 2; V3 USDG read 12% = two V4 hubs)
  // and left the deployments actually running the campaigns at 0%.
  keyByReserveToken: true,
  resolveAsset: (opp) => {
    const explorer = opp.explorerAddress?.toLowerCase()

    // V4 names its deployment directly: `explorerAddress` is the spoke (or hub)
    // contract, which is exactly how V4 lender keys are formed — the Etherfi
    // spoke campaign points at 0xbF10BDfE…, i.e. AAVE_V4_BF10BDFE…. Asset comes
    // from the deep link as usual, so these stay lender-keyed and land straight
    // on the right market.
    if (opp.type?.startsWith('AAVE_V4')) {
      const underlying = resolveUnderlyingFromOpportunity(opp)
      // `AAVE_V4_HUB_NET_LENDING` carries no explorerAddress — the hub is named
      // only in prose ("Aave V4 Paxos Hub"), and guessing from prose is what
      // this whole change exists to stop. Drop it: an unattributed campaign
      // costs one missing incentive, a misattributed one corrupts a rate.
      if (!explorer || !underlying) return undefined
      return {
        asset: underlying,
        lender: `AAVE_V4_${explorer.slice(2).toUpperCase()}`,
        keyBy: 'lender',
      }
    }

    // V3 family: `explorerAddress` is the aToken on a LEND opp and the variable
    // debt token on a BORROW opp — verified on-chain, 0xE3190143… is
    // `aHorRwaRLUSD` and 0x4139EcBe… is `variableDebtHorRwaUSDC`. Either one
    // belongs to exactly one reserve of exactly one deployment.
    if (explorer) return { asset: explorer }

    // No explorerAddress: the reserve token is the single `tokens` entry that
    // isn't the underlying. Drop when that's ambiguous.
    const underlying = resolveUnderlyingFromOpportunity(opp)
    const candidates = (opp.tokens ?? []).filter(
      (t) => t.address.toLowerCase() !== underlying,
    )
    if (candidates.length !== 1) return undefined
    return { asset: candidates[0].address.toLowerCase() }
  },
})

// ---------------------------------------------------------------------------
// Euler
// ---------------------------------------------------------------------------

/**
 * Extract an EVK vault address from an Euler app deep link.
 *
 *  - `/borrow?network=…&search=0x<vault>`      → the borrowable vault
 *  - `/positions/0x<collateral>/0x<borrow>`    → the vault being borrowed FROM
 *  - `/vault/0x<vault>?network=…`              → that vault
 */
function parseEulerVaultFromUrl(depositUrl?: string): string | undefined {
  if (!depositUrl) return undefined
  try {
    const url = new URL(depositUrl)
    const search = url.searchParams.get('search')
    if (search && /^0x[0-9a-fA-F]{40}$/.test(search))
      return search.toLowerCase()

    const positions = url.pathname.match(
      /\/positions\/0x[0-9a-fA-F]{40}\/(0x[0-9a-fA-F]{40})/,
    )
    if (positions) return positions[1].toLowerCase()

    const vault = url.pathname.match(/\/vault\/(0x[0-9a-fA-F]{40})/)
    if (vault) return vault[1].toLowerCase()
  } catch {
    // not a valid URL, caller falls back to explorerAddress
  }
  return undefined
}

/**
 * Euler v2 markets are EVK vaults, keyed as `EULER_V2:<chain>:<vaultAddress>`,
 * so every resolution below must land on an EVault address.
 *
 * No `chainIds` — Euler adds chains faster than a hardcoded list can track
 * (the previous 9-chain list dropped every Monad and HyperEVM campaign, ~73% of
 * the live book). One unfiltered sweep covers all of them; anything we don't
 * track simply never joins a market.
 *
 * Per opportunity `type`:
 *  - `EULER` + LEND — `explorerAddress` IS the EVault.
 *  - `EULER` + BORROW — `explorerAddress` is the vault's **debt token**
 *    (`eUSDC-15-DEBT`), which is NOT a key we hold. The real vault is in the
 *    `?search=` param of the deep link, so URL resolution comes first.
 *  - `EULER_BORROW_FROM_COLLATERAL` / `EULER_MULTI_BORROW_FROM_COLLATERAL` —
 *    `explorerAddress` is the borrow-side EVault, and the deep link's second
 *    `/positions/` segment agrees with it.
 *  - `MULTILENDBORROW` — a pooled campaign over several vaults with no
 *    `explorerAddress` at all; `tokens` lists the covered EVaults (plus the
 *    underlyings). Rewards are distributed pro-rata over the pooled TVL, so the
 *    campaign APR applies to each vault. We emit every non-debt token and let
 *    the registry join discard the underlyings.
 *  - `ERC20LOGPROCESSOR` — Euler **Earn** vaults. Handled by the default path,
 *    but `euler-vaults.json` carries no Earn vaults today so these produce
 *    orphan keys until an Earn registry lands.
 */
export const eulerMerklRewardFetcher = createMerklRewardFetcher({
  merklProtocolId: 'euler',
  lenderKey: 'EULER_V2',
  resolveAsset: (opp) => {
    if (opp.type === 'MULTILENDBORROW') {
      return (opp.tokens ?? [])
        .filter((t) => !t.symbol?.endsWith('-DEBT'))
        .map((t) => ({ asset: t.address.toLowerCase() }))
    }

    // Borrow opps point `explorerAddress` at the debt token — trust the deep
    // link first. Lend opps have no `search`/`positions` link, so they fall
    // through to `explorerAddress`, which is already the EVault.
    const vault =
      (opp.action === 'BORROW'
        ? parseEulerVaultFromUrl(opp.depositUrl)
        : undefined) ?? opp.explorerAddress?.toLowerCase()
    if (!vault) return undefined
    return { asset: vault }
  },
})

// ---------------------------------------------------------------------------
// Morpho — collateral incentives ONLY
// ---------------------------------------------------------------------------

/** Extract the 32-byte Morpho market uniqueKey from a Merkl deposit URL. */
function parseMorphoUniqueKey(depositUrl?: string): string | undefined {
  if (!depositUrl) return undefined
  const m = depositUrl.match(/\/market\/(0x[0-9a-fA-F]{64})/)
  return m?.[1]?.toLowerCase()
}

/**
 * Morpho supply/borrow rewards already arrive via Morpho's GraphQL API, so this
 * fetcher emits ONLY the collateral dimension (`type === "MORPHOCOLLATERAL"`) —
 * "deposit X as collateral" incentives that GraphQL doesn't expose. In our model
 * each Morpho market is stored as two per-side rows; the collateral token is its
 * own row (`MORPHO_BLUE_<uniqueKey>:<chain>:<collateralToken>`), so a collateral
 * incentive is simply a `deposit` reward on that row — which the pairs view reads
 * as `reward_apr_long` for the long/collateral leg. The Merkl action for these
 * opps is inconsistent (LEND on some chains, BORROW on others), so we force the
 * deposit bucket.
 *
 * No `chainIds` — Morpho ships on new chains faster than a hardcoded list can
 * follow; the `MORPHOCOLLATERAL` filter below already narrows the sweep.
 */
export const morphoCollateralMerklRewardFetcher = createMerklRewardFetcher({
  merklProtocolId: 'morpho',
  lenderKey: 'MORPHO_BLUE',
  resolveAsset: (opp) => {
    if (opp.type !== 'MORPHOCOLLATERAL') return undefined
    const uniqueKey = parseMorphoUniqueKey(opp.depositUrl)
    const collateral = opp.explorerAddress?.toLowerCase()
    if (!uniqueKey || !collateral) return undefined
    return {
      asset: collateral,
      lender: `MORPHO_BLUE_${uniqueKey.slice(2).toUpperCase()}`,
      bucket: 'deposit',
    }
  },
})
